import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useTranslation } from "react-i18next";
import { useGetEventQuery } from "@/state/eventsApi";
import { useSubmitEventRegistrationMutation } from "@/state/submissionsApi";
import type { UpcomingEvent, PastEvent } from "@/types/events";

const RegistrationSchema = z.object({
  name: z.string().min(1, "Full name is required"),
  email: z.email("Invalid email address"),
  company: z.string().optional(),
  phone: z.string().optional(),
});

type RegistrationValues = z.infer<typeof RegistrationSchema>;

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetEventQuery(id!);
  const [submitRegistration, { isLoading: submitting }] = useSubmitEventRegistrationMutation();
  const [submitted, setSubmitted] = useState(() => {
    try {
      const registered = JSON.parse(localStorage.getItem("aip_registered_events") ?? "[]");
      return Array.isArray(registered) && registered.includes(id);
    } catch {
      return false;
    }
  });
  const [submitError, setSubmitError] = useState("");

  const { register, handleSubmit, formState: { errors }, reset } = useForm<RegistrationValues>({
    resolver: zodResolver(RegistrationSchema),
    defaultValues: { name: "", email: "", company: "", phone: "" },
  });

  const onSubmit = async (formData: RegistrationValues) => {
    if (!event) return;
    setSubmitError("");
    try {
      await submitRegistration({
        eventId: event.id,
        eventTitle: event.title,
        name: formData.name,
        email: formData.email,
        company: formData.company || undefined,
        phone: formData.phone || undefined,
      }).unwrap();
      setSubmitted(true);
      reset();
      try {
        const existing = JSON.parse(localStorage.getItem("aip_registered_events") ?? "[]");
        const updated = Array.isArray(existing) ? [...new Set([...existing, event.id])] : [event.id];
        localStorage.setItem("aip_registered_events", JSON.stringify(updated));
      } catch {
        // localStorage unavailable, ignore
      }
    } catch (err) {
      const code = (err as any)?.data?.error?.code;
      if (code === "CAPACITY_REACHED") {
        setSubmitError("This event is full.");
      } else if (code === "REGISTRATION_CLOSED") {
        setSubmitError("Registration for this event is closed.");
      } else {
        const message = (err as any)?.data?.error?.message;
        setSubmitError(message ?? "Something went wrong. Please try again.");
      }
    }
  };

  if (isLoading) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#00C2C2]" />
        </div>
        <Footer />
      </main>
    );
  }

  if (isError || !data?.data) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mb-4 text-2xl">
            <i className="ri-calendar-close-line text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-[#0A1628] mb-2">{t("notFoundTitle")}</h2>
          <p className="text-gray-500 text-sm mb-6">{t("notFoundPrompt")}</p>
          <Link to="/events" className="bg-[#0A1628] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#0A1628] transition-colors whitespace-nowrap cursor-pointer">
            {t("eventCtaUpcoming")}
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const event = data.data;
  const isPast = "attendees" in event;
  const isUpcoming = "registrationOpen" in event;
  const upcomingEvent = isUpcoming ? (event as UpcomingEvent) : null;
  const pastEvent = isPast ? (event as PastEvent) : null;

  const eventDate = new Date(event.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <main>
      <Helmet>
        <title>{event.title} | {eventDate} | AIP Events</title>
        <meta name="description" content={`${event.description?.slice(0, 155) ?? `Join ${event.title} — an investment event in ${event.location} on ${eventDate}.`}`} />
        <link rel="canonical" href={`https://africainvestmentportal.com/events/${event.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${event.title} | ${eventDate}`} />
        <meta property="og:description" content={event.description?.slice(0, 155) ?? `Investment event in ${event.location} on ${eventDate}.`} />
        <meta property="og:url" content={`https://africainvestmentportal.com/events/${event.id}`} />
        {event.image && <meta property="og:image" content={event.image} />}
      </Helmet>
      <Navbar />

      <section className="relative pt-32 pb-0 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0">
          {event.image && (
            <img src={event.image} alt={event.title} className="w-full h-full object-cover object-top opacity-25" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 to-[#0A1628]/60" />
        </div>
        <div className="relative z-10 w-full px-6 lg:px-12 pb-16 pt-4">
          <Link to="/events" className="inline-flex items-center gap-2 text-white/50 text-sm hover:text-white transition-colors mb-8 cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line" /> {t("detailBack")}
          </Link>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#0A1628] text-white">{event.type}</span>
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${event.format === "Online" ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/70"}`}>
              {event.format}
            </span>
            {isPast && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-500/20 text-gray-300">{t("pastTag")}</span>}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white max-w-3xl leading-tight mb-4">{event.title}</h1>
          <div className="flex flex-wrap gap-5 text-white/60 text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-[#00C2C2]" />
              {new Date(event.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-2-line text-[#00C2C2]" />
              {event.location}
            </span>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="w-full px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {event.image && (
                <img src={event.image} alt={event.title} className="w-full h-72 object-cover object-top rounded-lg mb-8" />
              )}

              <h2 className="text-xl font-bold text-[#0A1628] mb-4">{t("detailAbout")}</h2>
              <p className="text-gray-600 leading-relaxed mb-8">{event.description}</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { icon: "ri-calendar-event-line", label: t("detailDate"), value: new Date(event.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) },
                  { icon: "ri-map-pin-2-line", label: t("detailLocation"), value: event.location },
                  { icon: "ri-building-line", label: t("detailSector"), value: event.sector },
                ].map((item) => (
                  <div key={item.label} className="bg-[#f8f9fc] rounded-lg p-4 border border-gray-100">
                    <div className="w-7 h-7 flex items-center justify-center text-[#00C2C2] mb-2"><i className={`${item.icon} text-base`} /></div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">{item.label}</p>
                    <p className="text-sm font-bold text-[#0A1628] leading-snug">{item.value}</p>
                  </div>
                ))}
              </div>

              {upcomingEvent?.agenda && upcomingEvent.agenda.length > 0 && (
                <>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-5">{t("detailAgenda")}</h3>
                  <div className="flex flex-col gap-6 mb-8">
                    {upcomingEvent.agenda.map((day) => (
                      <div key={day.day} className="rounded-xl border border-gray-100 overflow-hidden">
                        <div className="bg-[#0A1628] px-5 py-3 flex items-center gap-3">
                          <span className="text-xs font-bold text-[#00C2C2] uppercase tracking-wide">Day {day.day}</span>
                          <span className="text-white/30 text-xs">·</span>
                          <span className="text-xs text-white/70">
                            {new Date(day.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                          </span>
                          {day.title && (
                            <>
                              <span className="text-white/30 text-xs">·</span>
                              <span className="text-xs font-semibold text-white">{day.title}</span>
                            </>
                          )}
                        </div>
                        <div className="flex flex-col divide-y divide-gray-50">
                          {day.sessions.map((session, si) => (
                            <div key={si} className="px-5 py-4 hover:bg-[#f8f9fc] transition-colors">
                              <div className="flex gap-4 items-start">
                                <span className="text-xs font-bold text-[#00C2C2] w-14 shrink-0 pt-0.5">{session.time}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-[#0A1628] font-medium mb-2">{session.title}</p>
                                  {session.speakers.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {session.speakers.map((speaker, spi) => (
                                        <div key={spi} className="flex items-center gap-2 bg-white border border-gray-100 rounded-full px-3 py-1.5 shadow-sm">
                                          {speaker.imageUrl ? (
                                            <img
                                              src={speaker.imageUrl}
                                              alt={speaker.name}
                                              className="w-6 h-6 rounded-full object-cover shrink-0"
                                            />
                                          ) : (
                                            <div className="w-6 h-6 rounded-full bg-[#0A1628]/15 text-[#00C2C2] text-xs font-bold flex items-center justify-center shrink-0">
                                              {speaker.name.charAt(0)}
                                            </div>
                                          )}
                                          <div className="min-w-0">
                                            <p className="text-xs font-semibold text-[#0A1628] leading-tight">{speaker.name}</p>
                                            {speaker.qualification && (
                                              <p className="text-[10px] text-gray-400 leading-tight truncate max-w-[140px]">{speaker.qualification}</p>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {pastEvent && (
                <div className="bg-[#f8f9fc] rounded-lg p-6 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t("detailAttendance")}</p>
                  <p className="text-4xl font-bold text-[#00C2C2]">{pastEvent.attendees.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 mt-1">{t("detailParticipants")}</p>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-[#f8f9fc] rounded-lg border border-gray-200 p-6 sticky top-24">
                {isPast ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-200 text-gray-400 text-xl mx-auto mb-3">
                      <i className="ri-calendar-close-line" />
                    </div>
                    <h3 className="font-bold text-[#0A1628] text-sm mb-2">{t("detailPastMessage")}</h3>
                    <p className="text-xs text-gray-500 mb-4">{t("detailPastDesc")}</p>
                    <Link to="/events" className="bg-[#0A1628] text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap cursor-pointer block text-center">
                      {t("detailPastCta")}
                    </Link>
                  </div>
                ) : submitted ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0A1628] text-white text-xl mx-auto mb-3">
                      <i className="ri-check-line" />
                    </div>
                    <p className="font-bold text-[#0A1628] text-sm mb-1">{t("detailFormSuccessTitle")}</p>
                    <p className="text-xs text-gray-500">{t("detailFormSuccessBody")}</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-base font-bold text-[#0A1628] mb-1">{t("detailRegisterTitle")}</h3>
                    <p className="text-xs text-gray-500 mb-6">{t("detailRegisterDesc")}</p>

                    {upcomingEvent?.capacity != null && (
                      <div className="mb-5">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{upcomingEvent.registered} {t("detailRegistered")}</span>
                          <span>{upcomingEvent.capacity - upcomingEvent.registered} {t("detailSpotsLeft")}</span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#0A1628] rounded-full"
                            style={{ width: `${(upcomingEvent.registered / upcomingEvent.capacity) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <form id="event-registration-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                      {submitError && (
                        <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</p>
                      )}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("detailFormName")} *</label>
                        <input type="text" placeholder="Jane Doe" {...register("name")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                        {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("detailFormEmail")} *</label>
                        <input type="email" placeholder="jane@company.com" {...register("email")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                        {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("detailFormCompany")}</label>
                        <input type="text" placeholder="Your company" {...register("company")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("detailFormPhone")}</label>
                        <input type="tel" placeholder="+250 7XX XXX XXX" {...register("phone")} className="text-sm border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                      </div>
                      <button type="submit" disabled={submitting} className="bg-[#0A1628] text-white text-sm font-bold py-3 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2">
                        {submitting ? <><i className="ri-loader-4-line animate-spin" /> {t("detailFormSubmitting")}</> : <><i className="ri-calendar-check-line" /> {t("detailFormSubmit")}</>}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
